import { markRaw } from 'vue'
import PrivateRouter from '@/models/router/private-router'
import PublicRouter from '@/models/router/public-router'

interface RawQueuedMessage {
  rawJson: string
  channel: 'public' | 'private'
  messageCount: number
}

export class WebSocketUpdateQueue {
  private queue: RawQueuedMessage[] = markRaw([])
  private privateRouter: PrivateRouter
  private publicRouter: PublicRouter

  private publicMessageCounter = 0
  private privateMessageCounter = 0
  private onOutOfSync: (() => void) | null = null

  // Self-processing loop state
  private isProcessing = false
  private budgetMs = 4 // Default time budget per frame

  constructor() {
    this.privateRouter = new PrivateRouter()
    this.publicRouter = new PublicRouter()
  }

  setOutOfSyncCallback(callback: () => void): void {
    this.onOutOfSync = callback
  }

  /**
   * Set the time budget for processing (called from render loop)
   */
  setBudget(budgetMs: number): void {
    this.budgetMs = budgetMs
  }

  enqueueRaw(rawJson: string, channel: 'public' | 'private', messageCount: number): void {
    this.queue.push({ rawJson, channel, messageCount })
    // Start self-processing loop if not already running
    if (!this.isProcessing) {
      this.isProcessing = true
      this.processLoop()
    }
  }

  /**
   * Self-running processing loop using requestAnimationFrame
   */
  private processLoop(): void {
    if (this.queue.length === 0) {
      this.isProcessing = false
      return
    }

    const startTime = performance.now()
    while (this.queue.length > 0) {
      if (performance.now() - startTime >= this.budgetMs) break
      this.processMessage(this.queue.shift()!)
    }

    // Continue loop if there are more messages
    if (this.queue.length > 0) {
      requestAnimationFrame(() => this.processLoop())
    } else {
      this.isProcessing = false
    }
  }

  processAll(): void {
    while (this.queue.length > 0) {
      this.processMessage(this.queue.shift()!)
    }
    this.isProcessing = false
    // Re-check in case new items were added during processing
    if (this.queue.length > 0 && !this.isProcessing) {
      this.isProcessing = true
      this.processLoop()
    }
  }

  private processMessage(message: RawQueuedMessage): void {
    this.checkIfOutOfSync(message.messageCount, message.channel)
    const payload = JSON.parse(message.rawJson)

    if (message.channel === 'public') {
      const typed = payload as { event: string; data: unknown }
      this.publicRouter.route(typed.event, typed.data)
    } else {
      const typed = payload as Record<string, { event: string; data: unknown }>
      for (const key in typed) {
        this.privateRouter.route(typed[key].event, typed[key].data)
      }
    }
  }

  private checkIfOutOfSync(messageCount: number, channel: 'public' | 'private'): void {
    const counter = channel === 'public' ? this.publicMessageCounter : this.privateMessageCounter
    if (counter === 0 || counter === messageCount - 1 || counter === messageCount) {
      if (channel === 'public') this.publicMessageCounter = messageCount
      else this.privateMessageCounter = messageCount
    } else {
      this.onOutOfSync?.()
      if (channel === 'public') this.publicMessageCounter = messageCount
      else this.privateMessageCounter = messageCount
    }
  }

  getPendingCount(): number {
    return this.queue.length
  }
}

let instance: WebSocketUpdateQueue | null = null
export function getWebSocketUpdateQueue(): WebSocketUpdateQueue {
  if (!instance) instance = new WebSocketUpdateQueue()
  return instance
}
